/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
import { EuiIcon } from '../icon';
var colorToClassNameMap = {
  subdued: 'euiExpression--subdued',
  primary: 'euiExpression--primary',
  secondary: 'euiExpression--secondary',
  accent: 'euiExpression--accent',
  warning: 'euiExpression--warning',
  danger: 'euiExpression--danger'
};
var textWrapToClassNameMap = {
  'break-word': null,
  truncate: 'euiExpression--truncate'
};
export var COLORS = keysOf(colorToClassNameMap);
var displayToClassNameMap = {
  inline: null,
  columns: 'euiExpression--columns'
};
export var EuiExpression = ({
  className,
  description,
  descriptionProps,
  value,
  valueProps,
  color = 'secondary',
  uppercase = true,
  isActive = false,
  display = 'inline',
  descriptionWidth = '20%',
  onClick,
  isInvalid = false,
  textWrap = 'break-word',
  ...rest
}) => {
  const calculatedColor = isInvalid ? 'danger' : color;
  const classes = classNames('euiExpression', className, {
    'euiExpression-isActive': isActive,
    'euiExpression-isClickable': onClick,
    'euiExpression-isUppercase': uppercase
  }, displayToClassNameMap[display], colorToClassNameMap[calculatedColor], textWrapToClassNameMap[textWrap]);
  const Component = onClick ? 'button' : 'span';
  const descriptionStyle = descriptionProps && descriptionProps.style;
  const customWidth = display === 'columns' && descriptionWidth ? {
    flexBasis: descriptionWidth,
    ...descriptionStyle
  } : undefined;
  const invalidIcon = isInvalid ? <EuiIcon className="euiExpression__icon" type="alert" color={calculatedColor} /> : undefined;
  return <Component className={classes} onClick={onClick} {...rest}>
      <span className="euiExpression__description" style={customWidth} {...descriptionProps}>
        {description}
      </span>{' '}
      {value && <span className="euiExpression__value" {...valueProps}>
          {value}
        </span>}
      {invalidIcon}
    </Component>;
};
EuiExpression.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * First part of the expression
     */

  /**
     * First part of the expression
     */
  description: PropTypes.node.isRequired,
  descriptionProps: PropTypes.any,

  /**
     * Second part of the expression
     */

  /**
     * Second part of the expression
     */
  value: PropTypes.node,
  valueProps: PropTypes.any,

  /**
     * Color of the `description`
     */

  /**
     * Color of the `description`
     */
  color: PropTypes.oneOf(["subdued", "primary", "secondary", "accent", "warning", "danger"]),

  /**
     * Should the `description` auto-uppercase?
     */

  /**
     * Should the `description` auto-uppercase?
     */
  uppercase: PropTypes.bool,

  /**
     * Adds an solid border at the bottom
     */

  /**
     * Adds an solid border at the bottom
     */
  isActive: PropTypes.bool,

  /**
     * Turns the component into a button and adds an editable style border at the bottom
     */

  /**
     * Turns the component into a button and adds an editable style border at the bottom
     */
  onClick: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.func, PropTypes.func.isRequired]), PropTypes.func]),

  /**
     * Sets the display style for the expression. Defaults to `inline`
     */

  /**
     * Sets the display style for the expression. Defaults to `inline`
     */
  display: PropTypes.oneOf(["inline", "columns"]),

  /**
     * Forces color to display as `danger` and shows an `alert` icon
     */

  /**
     * Forces color to display as `danger` and shows an `alert` icon
     */
  isInvalid: PropTypes.bool,

  /**
     * Sets a custom width for the description when using the columns layout.
     * Set to a number for a custom width in `px`.
     * Set to a string for a custom width in custom measurement.
     * Defaults to `20%`
     */

  /**
     * Sets a custom width for the description when using the columns layout.
     * Set to a number for a custom width in `px`.
     * Set to a string for a custom width in custom measurement.
     * Defaults to `20%`
     */
  descriptionWidth: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),

  /**
     * Sets how to handle the wrapping of long text.
     */

  /**
     * Sets how to handle the wrapping of long text.
     */
  textWrap: PropTypes.oneOf(["break-word", "truncate"])
};

try {
  EuiExpression.__docgenInfo = {
    description: '',
    displayName: 'EuiExpression',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      description: {
        defaultValue: null,
        description: 'First part of the expression',
        name: 'description',
        parent: undefined,
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      descriptionProps: {
        defaultValue: null,
        description: '',
        name: 'descriptionProps',
        parent: undefined,
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLSpanElement>'
        }
      },
      value: {
        defaultValue: null,
        description: 'Second part of the expression',
        name: 'value',
        parent: undefined,
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      valueProps: {
        defaultValue: null,
        description: '',
        name: 'valueProps',
        parent: undefined,
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLSpanElement>'
        }
      },
      color: {
        defaultValue: {
          value: 'secondary'
        },
        description: 'Color of the `description`',
        name: 'color',
        parent: undefined,
        required: false,
        type: {
          name: 'enum',
          raw: '"subdued" | "secondary" | "accent" | "danger" | "warning" | "primary"',
          value: [{
            value: '"subdued"'
          }, {
            value: '"secondary"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"primary"'
          }]
        }
      },
      uppercase: {
        defaultValue: {
          value: 'true'
        },
        description: 'Should the `description` auto-uppercase?',
        name: 'uppercase',
        parent: undefined,
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isActive: {
        defaultValue: {
          value: 'false'
        },
        description: 'Adds an solid border at the bottom',
        name: 'isActive',
        parent: undefined,
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClick: {
        defaultValue: null,
        description: 'Turns the component into a button and adds an editable style border at the bottom',
        name: 'onClick',
        parent: undefined,
        required: false,
        type: {
          name: '((event: MouseEvent<HTMLButtonElement, MouseEvent>) => void) | (((event: MouseEvent<HTMLButtonElement, MouseEvent>) => void) & ((event: MouseEvent<...>) => void))'
        }
      },
      display: {
        defaultValue: {
          value: 'inline'
        },
        description: 'Sets the display style for the expression. Defaults to `inline`',
        name: 'display',
        parent: undefined,
        required: false,
        type: {
          name: 'enum',
          raw: '"inline" | "columns"',
          value: [{
            value: '"inline"'
          }, {
            value: '"columns"'
          }]
        }
      },
      isInvalid: {
        defaultValue: {
          value: 'false'
        },
        description: 'Forces color to display as `danger` and shows an `alert` icon',
        name: 'isInvalid',
        parent: undefined,
        required: false,
        type: {
          name: 'boolean'
        }
      },
      descriptionWidth: {
        defaultValue: {
          value: '20%'
        },
        description: 'Sets a custom width for the description when using the columns layout.\nSet to a number for a custom width in `px`.\nSet to a string for a custom width in custom measurement.\nDefaults to `20%`',
        name: 'descriptionWidth',
        parent: undefined,
        required: false,
        type: {
          name: 'string | number'
        }
      },
      textWrap: {
        defaultValue: {
          value: 'break-word'
        },
        description: 'Sets how to handle the wrapping of long text.',
        name: 'textWrap',
        parent: undefined,
        required: false,
        type: {
          name: 'enum',
          raw: '"truncate" | "break-word"',
          value: [{
            value: '"truncate"'
          }, {
            value: '"break-word"'
          }]
        }
      }
    },
    extendedInterfaces: ['ButtonHTMLAttributes', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}